'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/navigation';
import { ConfirmModal } from '@/shared/components/confirm-modal';

/**
 * Gardă de navigație la nivel de APLICAȚIE pentru Next 15 App Router.
 *
 * De ce e nevoie de ea: App Router nu are blocare nativă de navigație, iar
 * `router.push`/`replace` NU se pot intercepta prin patch pe `history.pushState`
 * (push-ul randează ruta ÎNAINTE de a chema pushState → patch-ul îngheață doar
 * bara de adrese peste conținut deja schimbat = stare ruptă). Singura cale
 * corectă e să interceptăm ÎNAINTE de dispatch: componentele de navigație cheamă
 * un router „cu gardă" (`useGuardedRouter`), iar link-urile `<a href>` sunt prinse
 * în faza de capture, înainte ca router-ul Next să ruleze.
 *
 * Provider-ul e INERT cât timp nicio pagină nu înregistrează un bloc activ
 * (`useNavigationBlock`). Nimic nu e interceptat în starea curată.
 *
 * Vectori acoperiți (Faza 1):
 *  - `router.push/replace/back` din TopBar + paleta de comenzi → `useGuardedRouter`;
 *  - click pe `<a href>` (Link din sidebar/breadcrumbs) → capture-phase click;
 *  - închidere/reîncărcare filă → `beforeunload`.
 * Butonul Back al browserului (popstate) NU e acoperit aici — Faza 2.
 */

interface BlockEntry {
  shouldBlock: boolean;
  message: string;
  title: string;
  actionLabel: string;
  cancelLabel: string;
  onConfirmLeave: () => void;
}

interface PendingLeave {
  proceed: () => void;
  message: string;
  title: string;
  actionLabel: string;
  cancelLabel: string;
  onConfirmLeave: () => void;
}

/** Textele unei plecări anume (ex. deconectarea), în locul celor ale blocului activ. */
export interface LeaveOverride {
  message?: string;
  actionLabel?: string;
}

/** Ce arată dialogul: textele blocului activ, cu excepțiile cerute de plecarea respectivă. */
export function leaveDialogText(
  block: Pick<BlockEntry, 'title' | 'message' | 'actionLabel' | 'cancelLabel'>,
  override?: LeaveOverride,
): Pick<BlockEntry, 'title' | 'message' | 'actionLabel' | 'cancelLabel'> {
  return {
    title: block.title,
    message: override?.message ?? block.message,
    actionLabel: override?.actionLabel ?? block.actionLabel,
    cancelLabel: block.cancelLabel,
  };
}

interface NavigationGuardValue {
  /**
   * Cere plecarea de pe pagină. Dacă un bloc e activ, arată dialogul de confirmare
   * și execută `proceed` doar la confirmare; altfel execută `proceed` imediat.
   */
  requestLeave: (proceed: () => void, override?: LeaveOverride) => void;
  registerBlock: (id: string, entry: BlockEntry) => void;
  unregisterBlock: (id: string) => void;
}

const NavigationGuardContext = createContext<NavigationGuardValue | null>(null);

const DEFAULT_TITLE = 'Ai modificări nesalvate';
// Implicit rămâne avertismentul TARE: default-ul se aplică acolo unde modificările chiar
// se pierd (roluri în setări, comandă în editare). Formularele cu ciornă locală trimit
// explicit `actionLabel: 'Părăsește pagina'` — plecarea de acolo nu pierde nimic.
const DEFAULT_ACTION = 'Renunță la modificări';
const DEFAULT_CANCEL = 'Continuă editarea';

export function NavigationGuardProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const blocksRef = useRef<Map<string, BlockEntry>>(new Map());
  const [pending, setPending] = useState<PendingLeave | null>(null);

  // ⚠ Primul bloc ACTIV câștigă (ordinea de inserție a Map-ului): dialogul arată
  // mesajul/etichetele LUI și rulează onConfirmLeave-ul LUI. Azi consumatorii
  // (roluri, factură, NIR, PO) nu pot fi activi simultan (rute/moduri dedicate).
  // Dacă adaugi un al doilea registrant CONCURENT pe aceeași pagină/modal,
  // regândește selecția (agregare sau prioritate explicită), altfel dialogul
  // poate afișa mesajul greșit și curăța starea greșită.
  const activeBlock = useCallback((): BlockEntry | null => {
    for (const entry of blocksRef.current.values()) {
      if (entry.shouldBlock) return entry;
    }
    return null;
  }, []);

  const requestLeave = useCallback(
    (proceed: () => void, override?: LeaveOverride) => {
      const block = activeBlock();
      if (!block) {
        proceed();
        return;
      }
      setPending({ proceed, ...leaveDialogText(block, override), onConfirmLeave: block.onConfirmLeave });
    },
    [activeBlock],
  );

  const registerBlock = useCallback((id: string, entry: BlockEntry) => {
    blocksRef.current.set(id, entry);
  }, []);

  const unregisterBlock = useCallback((id: string) => {
    blocksRef.current.delete(id);
  }, []);

  // ── Capture-phase anchor interceptor (Link din sidebar/breadcrumbs) ──
  // Prins ÎNAINTE ca router-ul Next să ruleze; activ doar cât un bloc e armat.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!activeBlock()) return;
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest('a[href]') as HTMLAnchorElement | null;
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return;
      }
      const url = new URL(href, window.location.href);
      if (url.origin !== window.location.origin) return;
      // no-op real (aceeași pagină + query identic) — lăsăm să treacă
      if (url.pathname + url.search === window.location.pathname + window.location.search) return;
      e.preventDefault();
      e.stopPropagation();
      requestLeave(() => router.push(href));
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [activeBlock, requestLeave, router]);

  // ── beforeunload (închidere/reîncărcare filă) ──
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!activeBlock()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [activeBlock]);

  const value = useMemo<NavigationGuardValue>(
    () => ({ requestLeave, registerBlock, unregisterBlock }),
    [requestLeave, registerBlock, unregisterBlock],
  );

  return (
    <NavigationGuardContext.Provider value={value}>
      {children}
      {pending && (
        <ConfirmModal
          title={pending.title}
          body={pending.message}
          actionLabel={pending.actionLabel}
          cancelLabel={pending.cancelLabel}
          danger
          onCancel={() => setPending(null)}
          onConfirm={() => {
            const target = pending;
            setPending(null);
            target.onConfirmLeave();
            target.proceed();
          }}
        />
      )}
    </NavigationGuardContext.Provider>
  );
}

interface UseNavigationBlockOptions {
  /** Corpul dialogului de confirmare (obligatoriu). */
  message: string;
  title?: string;
  actionLabel?: string;
  cancelLabel?: string;
  /** Rulează la confirmarea plecării, ÎNAINTE de navigare (ex. golește starea dirty). */
  onConfirmLeave?: () => void;
}

/**
 * Înregistrează un bloc de navigație cât timp `shouldBlock` e true. Cât e activ,
 * orice plecare de pe pagină (router.push din TopBar/paletă, click pe Link,
 * închidere filă) cere confirmare prin dialogul centralizat al provider-ului.
 */
export function useNavigationBlock(shouldBlock: boolean, options: UseNavigationBlockOptions) {
  const ctx = useContext(NavigationGuardContext);
  const id = useId();
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const {
    message,
    title = DEFAULT_TITLE,
    actionLabel = DEFAULT_ACTION,
    cancelLabel = DEFAULT_CANCEL,
  } = options;

  useEffect(() => {
    if (!ctx) return;
    ctx.registerBlock(id, {
      shouldBlock,
      message,
      title,
      actionLabel,
      cancelLabel,
      onConfirmLeave: () => optionsRef.current.onConfirmLeave?.(),
    });
    return () => ctx.unregisterBlock(id);
  }, [ctx, id, shouldBlock, message, title, actionLabel, cancelLabel]);
}

/**
 * Router „cu gardă": aceeași formă ca `useRouter()`, dar `push`/`replace`/`back`
 * trec prin blocul de navigație activ (dacă există) înainte de a naviga. Cât
 * nimic nu e blocat, se comportă identic cu router-ul brut. `prefetch`/`forward`/
 * `refresh` nu sunt navigări de plecare → nu se gardează.
 */
export function useGuardedRouter() {
  const router = useRouter();
  const ctx = useContext(NavigationGuardContext);
  return useMemo(() => {
    const request = ctx ? ctx.requestLeave : (proceed: () => void) => proceed();
    return {
      push: (...args: Parameters<typeof router.push>) => request(() => router.push(...args)),
      replace: (...args: Parameters<typeof router.replace>) => request(() => router.replace(...args)),
      back: () => request(() => router.back()),
      forward: () => router.forward(),
      refresh: () => router.refresh(),
      prefetch: (...args: Parameters<typeof router.prefetch>) => router.prefetch(...args),
    };
  }, [router, ctx]);
}

/**
 * Acces imperativ la garda de navigație, pentru plecări care nu sunt nici Link,
 * nici router.push — ex. schimbarea unui tab in-page care demontează conținut cu
 * modificări nesalvate. `requestLeave(proceed)` arată dialogul dacă un bloc e
 * activ, altfel rulează `proceed` imediat.
 */
export function useNavigationLeave() {
  const ctx = useContext(NavigationGuardContext);
  const requestLeave = useCallback(
    (proceed: () => void, override?: LeaveOverride) => {
      if (ctx) ctx.requestLeave(proceed, override);
      else proceed();
    },
    [ctx],
  );
  return { requestLeave };
}
